<div class="bg-white rounded-lg border border-gray-200 p-4 hover:shadow-md transition-shadow group cursor-pointer"
    (click)="onDetailsClick()">
    <!-- Header with validation status -->
    <div class="flex items-start justify-between mb-3">
        <div class="flex-1">
            <div class="flex items-center gap-2 mb-1">
                <h3 class="text-lg font-semibold text-gray-900">{{ zone.name }}</h3>
                <!-- Validation status indicator -->
                @if (zone.isValid === false) {
                <button type="button" (click)="onInfoClick($event); $event.stopPropagation()"
                    class="flex items-center justify-center w-5 h-5 rounded-full bg-red-100 hover:bg-red-200 transition-colors"
                    title="Show validation errors">
                    <svg class="h-3 w-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd"
                            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                            clip-rule="evenodd" />
                    </svg>
                </button>
                } @else if (zone.isValid === true) {
                <div class="flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
                    <svg class="h-3 w-3 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clip-rule="evenodd" />
                    </svg>
                </div>
                }
            </div>
            @if (zone.description) {
            <p class="text-sm text-gray-500 mt-1 line-clamp-2">{{ zone.description }}</p>
            }
        </div>
    </div>

    <!-- Zone Image Container -->
    <div class="mb-4 relative zone-image-wrapper">
        <!-- Hidden file input -->
        <input type="file" #fileInput class="hidden" accept="image/jpeg,image/png,image/gif,image/webp"
            (click)="$event.stopPropagation()" (change)="onFileSelected($event)" />

        <jjames-image-container size="full" aspectRatio="landscape" [imageSrc]="getZoneImage()"
            altText="Parking zone image" placeholderText="Zone Image" [showPlaceholderIcon]="true" [rounded]="true"
            [border]="true">
        </jjames-image-container>

        <!-- Upload overlay button -->
        <div class="absolute inset-0 flex items-center justify-center hover:bg-black/40 transition-all duration-200 rounded-lg cursor-pointer group"
            (click)="onUploadClick(); $event.stopPropagation()">
            @if (isUploading()) {
            <mat-spinner diameter="32"></mat-spinner>
            } @else {
            <div
                class="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col items-center text-white drop-shadow-lg">
                <svg class="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <span class="text-sm mt-1">{{ getZoneImage() ? 'Change Image' : 'Upload Image' }}</span>
            </div>
            }
        </div>
    </div>

    <!-- Zone Information Section -->
    <div class="space-y-3 mb-4">
        <!-- Governance Type -->
        <div class="flex items-center justify-between">
            <span class="text-sm text-gray-500">Governance:</span>
            @if (zone.governanceType) {
            <jjames-badge [variant]="getGovernanceTypeClass()" size="sm">
                {{ getGovernanceTypeLabel() }}
            </jjames-badge>
            } @else {
            <span class="text-sm text-gray-400 italic">-</span>
            }
        </div>

        <!-- Capacity -->
        <div class="flex items-center justify-between">
            <span class="text-sm text-gray-500">Capacity:</span>
            @if (zone.capacity) {
            <div class="flex items-center text-sm font-medium text-gray-900">
                <svg class="h-4 w-4 mr-1 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <span class="whitespace-nowrap">{{ zone.capacity }} spaces</span>
            </div>
            } @else {
            <span class="text-sm text-gray-400 italic">-</span>
            }
        </div>
    </div>


    <!-- Location -->
    <div class="flex items-center text-sm text-gray-500 mb-4">
        <svg class="h-4 w-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        @if (zone.address?.firstAddressLine || zone.address?.locality) {
        <span>{{ zone.address?.firstAddressLine || '' }} {{ zone.address?.locality || '' }}</span>
        } @else {
        <span class="text-gray-400 italic">-</span>
        }
    </div>

    <!-- Action Buttons -->
    <div class="action-buttons flex flex-wrap justify-end items-center gap-2" (click)="$event.stopPropagation()">
        @if (isAdmin()) {
        <div class="action-btn">
            <jjames-button variant="action-edit" size="md" tooltip="Edit parking zone"
                svgIcon="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                (clicked)="onEditClick()">
                <span class="action-btn-text">Edit</span>
            </jjames-button>
        </div>
        }
        @if (isAdmin()) {
        <div class="action-btn">
            <jjames-button variant="action-unassign" size="md" tooltip="Unassign parking zone"
                svgIcon="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                (clicked)="onUnassignClick()">
                <span class="action-btn-text">Unassign</span>
            </jjames-button>
        </div>

        <div class="action-btn">
            <jjames-button variant="action-delete" size="md" tooltip="Delete parking zone"
                svgIcon="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1-1H8a1 1 0 00-1 1v3M4 7h16"
                (clicked)="onDeleteClick()">
                <span class="action-btn-text">Delete</span>
            </jjames-button>
        </div>
        }
        <div class="action-btn">
            <jjames-button variant="action-details" size="md" tooltip="View parking zone details" svgIcon="M9 5l7 7-7 7"
                (clicked)="onDetailsClick()">
                <span class="action-btn-text">Details</span>
            </jjames-button>
        </div>
    </div>

    <!-- Validation Errors Alert -->
    @if (showValidationErrors && zone.validationErrors && zone.validationErrors.length > 0) {
    <div class="fixed top-4 right-4 z-50 max-w-md" (click)="$event.stopPropagation()">
        <jjames-alert type="error" [dismissible]="true" (dismissed)="onCloseAlert()">
            <div class="mb-2">
                <strong>Validation Errors for {{ zone.name }}:</strong>
            </div>
            <ul class="list-disc list-inside space-y-1">
                @for (error of zone.validationErrors; track error) {
                <li class="text-sm">{{ error }}</li>
                }
            </ul>
        </jjames-alert>
    </div>
    }
</div>